import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ChevronDown, X, Square } from "lucide-react";
import { subscribeBadges, cancelBadge, cancelAll } from "@/lib/tabBadge";
import { toast } from "sonner";

/**
 * "Now cooking…" header pill — aggregated view of every background task
 * currently in flight (Suno song / Sora 2 video / stem extraction / voice
 * clone training). Renders nothing when the badge stack is empty so the
 * header stays clean during idle moments.
 *
 * Subscribes to the shared tabBadge store via `subscribeBadges`, which
 * snapshots the stack on every push/update/clear. Clicking the pill opens
 * an inline dropdown listing each task — its color dot, label, and live
 * elapsed seconds. Closes on outside-click + Escape.
 *
 * Lightweight on purpose: no React Context, no global event-bus —
 * just a 1-callback subscription that fires when the stack mutates.
 */
export default function NowCookingPill() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => subscribeBadges(setItems), []);

  // Close the dropdown the moment the stack drains.
  useEffect(() => {
    if (items.length === 0) setOpen(false);
  }, [items.length]);

  // Outside-click + Escape close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (items.length === 0) return null;

  // Most-recent task wins the pill's accent color. Aggregate elapsed
  // shows the longest-running task — gives a sense of how deep the
  // queue is.
  const top = items[items.length - 1];
  const maxElapsed = items.reduce((acc, b) => Math.max(acc, b.elapsed ?? 0), 0);

  return (
    <div ref={wrapRef} className="relative" data-testid="now-cooking-pill-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="now-cooking-pill"
        className="flex items-center gap-2 rounded-full border border-white/15 bg-black/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/85 shadow-lg shadow-black/40 backdrop-blur transition hover:bg-black/70"
        style={{ boxShadow: `0 0 12px ${top.color}55, inset 0 0 1px ${top.color}66` }}
        title="Active background tasks — click for details"
      >
        {/* Pulsing dot — same color as the top badge */}
        <span className="relative grid h-2 w-2 place-items-center">
          <span
            className="absolute inset-0 animate-ping rounded-full opacity-60"
            style={{ backgroundColor: top.color }}
          />
          <span
            className="relative h-2 w-2 rounded-full"
            style={{ backgroundColor: top.color }}
          />
        </span>
        <Sparkles className="h-3 w-3" style={{ color: top.color }} />
        <span>
          {items.length === 1 ? "Now cooking" : `${items.length} cooking`}
          <span className="ml-1.5 tabular-nums text-white/55">{maxElapsed}s</span>
        </span>
        <ChevronDown className={`h-3 w-3 text-white/55 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
          key="nowcookingpill-overlay"
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            data-testid="now-cooking-dropdown"
            className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-2xl border border-white/15 bg-zinc-950/95 shadow-2xl shadow-black/60 backdrop-blur-xl"
          >
            <div className="border-b border-white/8 px-4 py-2.5">
              <p className="font-display text-sm font-bold tracking-tight text-white">In the oven</p>
              <p className="mt-0.5 font-mono text-[9px] uppercase tracking-wider text-white/45">
                {items.length} task{items.length === 1 ? "" : "s"} running in the background
              </p>
            </div>
            <ul className="max-h-72 overflow-y-auto">
              {items.slice().reverse().map((b) => (
                <li
                  key={b.id}
                  data-testid={`now-cooking-item-${b.id}`}
                  className="flex items-center gap-3 border-b border-white/5 px-4 py-2.5 last:border-b-0"
                >
                  <span
                    className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg"
                    style={{ backgroundColor: `${b.color}22`, boxShadow: `inset 0 0 0 1px ${b.color}40` }}
                  >
                    <span
                      className="h-2 w-2 animate-pulse rounded-full"
                      style={{ backgroundColor: b.color }}
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-medium text-white">{b.label}</p>
                    <p className="font-mono text-[9px] uppercase tracking-wider text-white/45">
                      Running · {b.elapsed != null ? `${b.elapsed}s elapsed` : "queued"}
                      {!b.cancellable && <span className="ml-1.5 text-white/30">· server-side</span>}
                    </p>
                  </div>
                  {b.cancellable && (
                    <button
                      type="button"
                      data-testid={`now-cooking-cancel-${b.id}`}
                      onClick={() => {
                        cancelBadge(b.id);
                        toast.message(`Stopped · ${b.label}`, { duration: 2200 });
                      }}
                      title="Cancel this task"
                      className="flex-shrink-0 rounded-md bg-white/[0.06] p-1.5 text-white/55 transition hover:bg-rose-500/20 hover:text-rose-200"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {/* Stop all — only renders if at least one cancellable task is
                in the stack. Server-side-only tasks (Suno, Sora 2) keep
                running upstream but we stop listening for their result. */}
            {items.some((b) => b.cancellable) && (
              <div className="border-t border-white/5 px-4 py-2">
                <button
                  type="button"
                  data-testid="now-cooking-stop-all"
                  onClick={() => {
                    const n = cancelAll();
                    if (n > 0) toast.message(`Stopped ${n} task${n === 1 ? "" : "s"}`, { duration: 2500 });
                  }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-rose-100 transition hover:bg-rose-500/25"
                >
                  <Square className="h-3 w-3 fill-current" />
                  Stop all cancellable tasks
                </button>
              </div>
            )}
            <div className="border-t border-white/5 px-4 py-2 font-mono text-[9px] uppercase tracking-wider text-white/35">
              Tab title + favicon also reflect this · feel free to switch tabs
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
